import { CollectionViewer } from '@angular/cdk/collections';
import { BehaviorSubject, delay, Observable, of } from 'rxjs';
import { MappingService } from '@services/mapping.service';
import { Sort, SortDirection } from '@angular/material/sort';
import { SensorNode } from '@app/shared/models/sensor-node';
import { SensorNodeService } from '@services/sensor-node.service';
import { ToastService } from '@services/toast/toast.service';

export interface UnmappedNode {
  nodeId: number;
  address: number;
  nodeType: string;
  gatewayName: string;
  gatewayId: number;
  buildingId: number;
  connected: boolean;
  roomName?: string;
}

export interface NodePoint extends UnmappedNode {
  dropPoint: { x: number; y: number };
}

export abstract class AbstractUnmappedNodesDataSource {
  protected nodesSubject = new BehaviorSubject<UnmappedNode[]>([]);
  protected loadingSubject = new BehaviorSubject(false);
  protected countSubject = new BehaviorSubject(0);
  protected roomNamesSubject = new BehaviorSubject<string[]>([]);
  sort: Sort = { active: 'address', direction: 'asc' };

  connect(collectionViewer: CollectionViewer): Observable<readonly UnmappedNode[]> {
    return this.nodesSubject.asObservable();
  }
  disconnect(collectionViewer: CollectionViewer): void {
    this.nodesSubject.next([]);
    this.countSubject.next(0);
    this.roomNamesSubject.next([]);
  }

  get isLoading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  get roomNames$(): Observable<string[]> {
    return this.roomNamesSubject.asObservable();
  }

  protected updateRoomNames(nodes: UnmappedNode[]): void {
    const distinct = Array.from(
      new Set(nodes.map((n) => n.roomName).filter((name): name is string => !!name))
    ).sort((a, b) => a.localeCompare(b));
    this.roomNamesSubject.next(distinct);
  }

  abstract loadUnmappedNodes(buildingId: number, filter?: Record<string, string | string[]>): void;
  abstract deleteDisconnectedNode(nodeId: number, buildingId: number): void;

  sortChange(sort: Sort): void {
    this.sort = sort;
    this.sortUnderlyingData(sort);
  }

  sortUnderlyingData(event: Sort): void {
    const { active, direction } = event;
    if (direction !== '') {
      const renderedData = this.nodesSubject.getValue();
      const sortedData = [
        ...renderedData.sort((left, right) => {
          const primary = this.sortByKeyAndDirection(active, direction, left, right);
          if (primary !== 0 || active === 'address') {
            return primary;
          }
          return this.sortByKeyAndDirection('address', 'asc', left, right);
        })
      ];
      this.nodesSubject.next(sortedData);
    }
  }

  private sortByKeyAndDirection(
    keyName: string,
    direction: SortDirection,
    left: UnmappedNode,
    right: UnmappedNode
  ): number {
    const leftValue = left[keyName];
    const rightValue = right[keyName];
    const leftMissing = leftValue == null || leftValue === '';
    const rightMissing = rightValue == null || rightValue === '';
    // ASC: nulls first, DESC: nulls last
    if (leftMissing && rightMissing) {
      return 0;
    }
    if (leftMissing) {
      return direction === 'asc' ? -1 : 1;
    }
    if (rightMissing) {
      return direction === 'asc' ? 1 : -1;
    }
    if (direction === 'asc') {
      if (leftValue < rightValue) {
        return -1;
      } else if (leftValue > rightValue) {
        return 1;
      }
      return 0;
    }
    if (direction === 'desc') {
      if (rightValue < leftValue) {
        return -1;
      } else if (rightValue > leftValue) {
        return 1;
      }
      return 0;
    }
  }
}

export class UnmappedNodesDataSource extends AbstractUnmappedNodesDataSource {
  private MIN_DELAY_FOR_LOADING_SPINNER = 750;
  constructor(
    private readonly mappingService: MappingService,
    private readonly sensorNodeService: SensorNodeService,
    private readonly toastService: ToastService
  ) {
    super();
  }

  loadUnmappedNodes(buildingId: number, filter?: Record<string, string | string[]>): void {
    this.loadingSubject.next(true);
    this.mappingService
      .getUnmappedNodesList(buildingId)
      .pipe(delay(this.MIN_DELAY_FOR_LOADING_SPINNER))
      .subscribe((nodes) => {
        this.updateRoomNames(nodes);
        if (filter) {
          nodes = nodes.filter((node) => {
            return Object.entries(filter).every(([key, filterValue]) => {
              if (Array.isArray(filterValue) && filterValue.length > 0) {
                return filterValue.some((value) => this.doesNodeMatchFilter(node, key, value));
              } else if (typeof filterValue === 'string') {
                return this.doesNodeMatchFilter(node, key, filterValue);
              }
              return false;
            });
          });
        }
        this.nodesSubject.next(nodes);
        if (this.sort) {
          this.sortUnderlyingData(this.sort);
        }
        this.loadingSubject.next(false);
        this.countSubject.next(nodes.length);
      });
  }

  private doesNodeMatchFilter(node: Partial<SensorNode>, key: string, value: string): boolean {
    const lowerCaseValue = value.toLowerCase();
    if (!lowerCaseValue) {
      return true;
    }

    if (key === 'roomName') {
      return (node[key] || '').toString().toLowerCase() === lowerCaseValue;
    }

    const nodeValue =
      key === 'address' && node[key] != null
        ? node[key].toString(16).toLowerCase()
        : (node[key] || 'sn3').toString().toLowerCase();

    return (key === 'nodeType' && lowerCaseValue === 'sn3') ? nodeValue === 'sn3' : nodeValue.includes(lowerCaseValue);
  }

  deleteDisconnectedNode(nodeId: number, buildingId: number): void {
    this.loadingSubject.next(true);
    this.sensorNodeService.deleteDisconnectedNode(nodeId, buildingId).subscribe({
      next: () => {
        const currentNodes = this.nodesSubject.getValue();
        const updatedNodes = currentNodes.filter((node) => node.nodeId !== nodeId);
        this.nodesSubject.next(updatedNodes);
        this.countSubject.next(updatedNodes.length);
        this.toastService.success({
          message: `Node deleted successfully ${nodeId}`,
          dataCy: 'disconnected-node-deleted-successfully'
        });
        this.loadingSubject.next(false);
      },
      error: () => {
        this.toastService.error({
          message: 'Error deleting unmapped node',
          dataCy: 'disconnected-node-deletion-error'
        });
        this.loadingSubject.next(false);
      }
    });
  }
}

export class MockUnmappedNodesDataSource extends AbstractUnmappedNodesDataSource {
  private MIN_DELAY_FOR_LOADING_SPINNER = 2200;
  constructor() {
    super();
  }

  private generateNodes(count: number, buildingId: number): UnmappedNode[] {
    const result: UnmappedNode[] = [];
    const nodeTypes = [
      SensorNode.SN3_NODE_TYPE,
      SensorNode.SN3_UPGRADE_NODE_TYPE,
      SensorNode.PASSIVE_NODE_TYPE,
      SensorNode.HIM84_NODE_TYPE,
      SensorNode.HCD405_NODE_TYPE,
      SensorNode.HCD038_NODE_TYPE
    ];
    const roomNames = ['Living Room', 'Kitchen', 'Bedroom', 'Bathroom', 'Office', null];
    while (count > 0) {
      const nodeType = nodeTypes[Math.floor(Math.random() * 3)];
      const address = Math.floor(Math.random() * 12000);
      const gatewayId = Math.floor(Math.random() * 500);
      const gatewayName = 'Gateway-' + gatewayId;
      const connected = true;
      const roomName = roomNames[Math.floor(Math.random() * roomNames.length)];
      result.push({ nodeType, address, gatewayId, gatewayName, buildingId, nodeId: address, connected, roomName });
      count--;
    }
    return result;
  }

  loadUnmappedNodes(buildingId: number): void {
    this.loadingSubject.next(true);
    const delayedObservable = of(this.generateNodes(150, buildingId)).pipe(delay(this.MIN_DELAY_FOR_LOADING_SPINNER));
    delayedObservable.subscribe((result) => {
      this.loadingSubject.next(false);
      this.updateRoomNames(result);
      this.nodesSubject.next(result);
      this.countSubject.next(result.length);
    });
  }

  deleteDisconnectedNode(nodeId: number, buildingId: number): void {}
}
